import type { AxiosError } from "axios";

export interface ApiErrorBody {
    statusCode: number;
    message: string | string[];
    /** Código de error del backend (ej. TEMPLATE_MANDATORY_FIELDS_MISSING). */
    code?: string;
}

function isValidMessage(message: unknown): message is string | string[] {
    return typeof message === "string" || (Array.isArray(message) && message.every((m) => typeof m === "string"));
}

export interface ApiErrorLike {
    url?: string;
    status: number;
    statusText: string;
    body: ApiErrorBody;
}

export function isApiErrorLike(error: unknown): error is ApiErrorLike {
    return (
        error instanceof Error &&
        typeof (error as unknown as ApiErrorLike).body === "object" &&
        (error as unknown as ApiErrorLike).body !== null &&
        typeof (error as unknown as ApiErrorLike).body?.statusCode === "number" &&
        isValidMessage((error as unknown as ApiErrorLike).body?.message)
    );
}

/** Tipo de error de API para uso en dominio y UI (sin depender de data-access/api/service). */
export type IApiError = Error & { body: ApiErrorBody };

export function getApiErrorFromAxios(error: unknown): ApiErrorLike | null {
    if (!error || typeof error !== "object") return null;
    const axiosError = error as AxiosError<ApiErrorBody>;
    if (axiosError.response?.data && typeof axiosError.response.data === "object") {
        const data = axiosError.response.data as ApiErrorBody;
        if (typeof data.statusCode === "number" && isValidMessage(data.message)) {
            return {
                url: axiosError.config?.url,
                status: axiosError.response.status,
                statusText: axiosError.response.statusText,
                body: {
                    statusCode: data.statusCode,
                    message: data.message,
                    ...(typeof data.code === "string" && { code: data.code }),
                },
            };
        }
    }
    return null;
}
